import React, { createContext, useState, useContext, RefObject, ReactNode } from 'react'

interface IIntersectionContext {
  navTitle?: string
  currentItem: string
  currentItems?: string[]
  addItem: (item: string) => void
  removeItem: (item: string) => void
  itemRefs: RefObject<HTMLDivElement>[]
  updateRefs: (ref: RefObject<HTMLDivElement>) => void
}

export const IntersectionContext = createContext<IIntersectionContext | undefined>(undefined)

type IIntersectionProvider = {
  navTitle?: string
  children: ReactNode
  initialItem?: string
}

export function IntersectionProvider({
  navTitle,
  children,
  initialItem = '',
}: IIntersectionProvider): React.ReactElement {
  const initialItems = initialItem ? [initialItem] : []
  const [currentItems, setCurrentItems] = useState(initialItems)
  const [itemRefs, setItemRefs] = useState<RefObject<HTMLDivElement>[]>([])

  function addItem(item: string) {
    setCurrentItems((prevItems) => {
      if (!prevItems.includes(item)) return [...prevItems, item]
      return prevItems
    })
  }

  function removeItem(item: string) {
    setCurrentItems((prevItems) => {
      if (prevItems.includes(item)) {
        const copy = [...prevItems]
        const indexToRemove = prevItems.indexOf(item)
        copy.splice(indexToRemove, 1)
        return copy
      }
      return prevItems
    })
  }

  function updateRefs(ref: RefObject<HTMLDivElement>) {
    setItemRefs((itemRefs) => [...itemRefs, ref])
  }

  return (
    <IntersectionContext.Provider
      value={{
        navTitle,
        currentItem: currentItems[currentItems.length - 1],
        currentItems,
        addItem,
        removeItem,
        itemRefs,
        updateRefs,
      }}
    >
      {children}
    </IntersectionContext.Provider>
  )
}

export const useIntersectionContext = (): IIntersectionContext => {
  const context = useContext(IntersectionContext)
  if (!context) {
    throw new Error('useIntersectionContext should be use within useIntersectionContext')
  }

  return context
}
